import Layout from 'components/layout';

import React, { type PropsWithChildren, useMemo, useState, type FC, type ChangeEvent, useEffect } from 'react';

import { useStaticQuery, graphql } from 'gatsby';
import {
  Box,
  Container,
  MultipleImageHero,
  Combobox,
  SimplePagination,
  AccessibleDescription,
  ResourceCard,
  LoadingIndicator,
  SearchBar,
} from '@lucid/components';
import { isBrowser, isDefined, withURL } from '@lucid/utils';
import { useUrlPathUpdate, useSmartlingStrings } from '@lucid/hooks';

import { useCaseStudySearch } from 'hooks/hooks';
import type { Lucid } from 'types/types';

// TYPES
export interface CollectionCard {
  /** The id of the directus item */
  id: string;
  /** The date of the webinar to be displayed above the `Card` heading */
  date?: string;
  /** The image to be displayed at the top of the `Card`. */
  image?: Lucid.Directus.Generated.Directus_Files;
  /** The text to be displayed in the heading of the `Card`. */
  heading?: string;
  /** The text to be displayed in the subheading of the `Card`. */
  subheading?: string;
  /** The url that the `Card` will link to. */
  url?: string;
  /** The Lucid products that are related to the `Card`. */
  products?: Lucid.Directus.Generated.Ebook_Lucid_Product[];
  /** The related industires related to the `Card` for filtering. */
  industries?: Lucid.Directus.Generated.Ebook_Collection_Industry[];
  /** The related teams related to the `Card` for filtering. */
  teams?: Lucid.Directus.Generated.Ebook_Collection_Team[];
  /** The related topics related to the `Card` for filtering. */
  topics?: Lucid.Directus.Generated.Ebook_Collection_Topic[];
}

interface CollectionBaseProps {
  /** The normalized card data */
  collectionCards: CollectionCard[];
  /** Page context for the Collection page */
  pageContext: Lucid.Context.HubPageProps['pageContext'];
  /** Translated data for the language picker */
  nodeTranslations: Lucid.Directus.Custom.NodeTranslation[];
  /** The Collection page Hero data */
  hero: Omit<Lucid.Directus.Generated.Dual_Image_Hero, 'id'>;
  /** If the Collection is a Case Study Hub */
  isCaseStudyHub?: boolean;
}

const CollectionBase: FC<PropsWithChildren<CollectionBaseProps>> = ({
  collectionCards,
  pageContext,
  nodeTranslations,
  hero,
  children,
  isCaseStudyHub,
}) => {
  const {
    shortCode,
    urlLangPrefix,
    localeId,
    pageType,
    nid,
    shouldHideSsr,
    page: ssrPage,
    pageSize,
    hubAlias,
    metatags,
    product,
  } = pageContext;
  const { filterCollections } = useStaticQuery(graphql`
    query {
      filterCollections: directus {
        collection_team {
          __typename
          name
        }
        collection_topic {
          __typename
          name
        }
        collection_industry {
          __typename
          name
        }
      }
    }
  `);
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_learning_campus_collection_filter_team',
      'mktg_learning_campus_collection_filter_industry',
      'mktg_learning_campus_collection_filter_topic',
      'mktg_ct_cs_search',
      'mktg_ct_cs_search_description',
      'mktg_generics_search_result_count',
      'mktg_read_more_about',
      'mktg_learning_campus_collection_tags',
    ],
    shortCode,
  });

  const [cards, setCards] = useState<CollectionCard[]>(collectionCards);
  const [collectionIndustry, setCollectionIndustry] = useState('');
  const [collectionTeam, setCollectionTeam] = useState('');
  const [collectionTopic, setCollectionTopic] = useState('');

  useEffect(() => {
    if (!collectionTopic) return setCards(collectionCards);

    const filteredCards = collectionCards.filter(card =>
      card.topics?.some(topic => topic?.collection_topic_id?.name === collectionTopic),
    );
    setCards(filteredCards);
  }, [collectionTopic, collectionCards]);

  // SEARCH
  const [page, setPage] = useState<number>(ssrPage);
  const searchBarRef = React.createRef<HTMLInputElement>();

  useUrlPathUpdate(withURL(`${hubAlias || ''}/${page && page > 1 ? `${page}` : ''}`, { urlLangPrefix, product }));
  const { isLoading, handleSearch, searchQuery } = useCaseStudySearch({
    initialPage: ssrPage,
    collectionCards,
    setCards,
    page,
    setPage,
    searchBarRef,
    collectionIndustry,
    collectionTeam,
  });

  const handleNewPage = (value: number) => {
    setPage(value);

    if (isBrowser()) {
      searchBarRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  };

  // FILTERS
  interface FilterItem extends Lucid.Directus.Custom.Node {
    __typename: 'DirectusData_collection_team' | 'DirectusData_collection_industry' | 'DirectusData_collection_topic';
    name: string;
  }

  type FilterGroups = {
    [K in FilterItem['__typename']]?: { value: string; label: string }[];
  };

  const filterItems: FilterItem[] = isCaseStudyHub
    ? ([...(filterCollections.collection_team ?? []), ...(filterCollections.collection_industry ?? [])] as FilterItem[])
    : (filterCollections.collection_topic ?? []).filter(item =>
        collectionCards.some(card =>
          card.topics?.map(({ collection_topic_id }) => collection_topic_id?.name).includes(item.name),
        ),
      );

  const filterGroups: FilterGroups = filterItems.reduce(
    (groups, { __typename, name }) => ({
      ...groups,
      [__typename]: [...(groups[__typename] || []), { value: name, label: name }],
    }),
    isCaseStudyHub
      ? {
          DirectusData_collection_team: [
            { value: '', label: withSmartling('mktg_learning_campus_collection_filter_team') },
          ],
          DirectusData_collection_industry: [
            { value: '', label: withSmartling('mktg_learning_campus_collection_filter_industry') },
          ],
        }
      : {
          DirectusData_collection_topic: [
            { value: '', label: withSmartling('mktg_learning_campus_collection_filter_topic') },
          ],
        },
  );

  const filtersArray = useMemo(
    () =>
      Object.entries(filterGroups).map(([key, value]) => {
        const filterType = key.replace('DirectusData_collection_', '');
        const collectionData = {
          team: {
            onChange: setCollectionTeam,
            currentSelection: collectionTeam,
          },
          industry: {
            onChange: setCollectionIndustry,
            currentSelection: collectionIndustry,
          },
          topic: {
            onChange: setCollectionTopic,
            currentSelection: collectionTopic,
          },
        }[filterType];

        return {
          onChange: collectionData?.onChange!,
          placeholder: withSmartling(`mktg_learning_campus_collection_filter_${filterType}`),
          ariaLabel: withSmartling(`mktg_learning_campus_collection_filter_${filterType}`),
          startValue: value.find(({ label }) => label === collectionData?.currentSelection)?.label,
          options: filterGroups[key],
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collectionTeam, collectionTopic, collectionIndustry],
  );

  return (
    <Layout
      localeId={localeId}
      metatags={metatags}
      nid={nid}
      nodeTranslations={nodeTranslations}
      pageType={pageType}
      product={product}
      shortCode={shortCode}
      shouldHideSsr={shouldHideSsr}
      urlLangPrefix={urlLangPrefix}
    >
      <MultipleImageHero
        shouldScaleImages
        css={theme => ({
          backgroundColor: theme.colors.coolGrey[5],
        })}
        {...hero}
        variant="light"
      />
      {children}
      <Container
        css={theme => ({
          paddingBottom: theme.space.s64,
          paddingTop: isCaseStudyHub ? theme.space.s48 : theme.space.s64,
          maxWidth: '750px',
          [theme.breakpoints.bp500.min]: {
            paddingBottom: theme.space.s120,
            paddingTop: isCaseStudyHub ? theme.space.s48 : theme.space.s120,
          },
          [theme.breakpoints.bp1220.min]: {
            maxWidth: '90rem',
          },
        })}
      >
        {isCaseStudyHub && (
          <Box
            css={theme => ({
              display: 'flex',
              justifyContent: 'center',
              paddingBottom: theme.space.s40,
              maxWidth: '100%',
              [theme.breakpoints.bp1220.min]: { justifyContent: 'flex-end', paddingBottom: theme.space.s64 },
            })}
          >
            <AccessibleDescription
              isForScreenReaderOnly
              id="collection-search-description"
              SRText={withSmartling('mktg_ct_cs_search_description')}
            />
            <SearchBar
              ref={searchBarRef}
              aria-describedby="collection-search-description"
              defaultValue={searchQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleSearch(e.target.value);
              }}
              placeholder={withSmartling('mktg_ct_cs_search')}
            />
          </Box>
        )}
        <Box
          css={theme => ({
            flex: '0 1 100%',
            display: 'flex',
            flexFlow: 'row wrap',
            rowGap: theme.space.s24,
            justifyContent: 'center',
            marginBottom: theme.space.s64,
            [theme.breakpoints.bp1220.min]: {
              rowGap: 0,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
            },
          })}
        >
          {filtersArray.map((filter, index) => (
            <Combobox
              key={`filter-${filter.options[0].value}`}
              css={theme => ({
                width: '400px',
                maxWidth: '100%',
                flexBasis: 'auto',
                flexGrow: 0,
                [theme.breakpoints.bp1220.min]: { width: '300px' },
              })}
              id={`filter-${index}`}
              onChange={optionSelected => filter.onChange(optionSelected.value)}
            >
              <Combobox.Box variant="thickBottomLine">
                <Combobox.Input
                  hasCarot
                  aria-label={filter.ariaLabel}
                  placeholder={filter.placeholder}
                  startValue={filter.startValue}
                />
              </Combobox.Box>
              <Combobox.List variant="blueHighlight">
                {filter.options.map(filter => (
                  <Combobox.Option key={filter.value} text={filter.label} value={filter.value} />
                ))}
              </Combobox.List>
            </Combobox>
          ))}
        </Box>
        <Box css={{ position: 'relative' }}>
          {(!page || isLoading) && (
            <Box
              aria-busy={isLoading}
              aria-live="polite"
              css={theme => ({
                zIndex: theme.zIndices.z1000,
                backgroundColor: theme.colors.white,
                position: 'absolute',
                top: 0,
                left: 0,
                right: `-${theme.space.s16}px`,
                bottom: 0,
              })}
            >
              <LoadingIndicator />
            </Box>
          )}
          {cards && (
            <>
              <AccessibleDescription
                isForScreenReaderOnly
                aria-live="polite"
                id="collection-search-count"
                SRText={withSmartling('mktg_generics_search_result_count', {
                  count: cards ? cards.length.toString() : '0',
                })}
              />
              <SimplePagination
                aria-describedby="collection-search-count"
                items={cards}
                itemsPerPage={pageSize}
                onClick={handleNewPage}
                startPage={page}
                urlAlias={`${hubAlias}/`}
              >
                {({ visibleItems }) => (
                  <Box
                    css={theme => ({
                      display: 'grid',
                      gridRowGap: theme.space.s40,
                      [theme.breakpoints.bp768.min]: {
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gridColumnGap: theme.space.s40,
                      },
                      [theme.breakpoints.bp1220.min]: {
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gridColumnGap: theme.space.s40,
                      },
                    })}
                  >
                    {visibleItems?.map(card => {
                      const cardImage = card.image as Lucid.Directus.Generated.Directus_Files;
                      const tags = card.products?.length
                        ? card.products?.map(rp => rp.lucid_product_id?.name as Lucid.Site.Name).filter(isDefined)
                        : undefined;

                      return (
                        <ResourceCard
                          key={`collection-base-resourceCard-${card.id}`}
                          shouldTruncateText
                          heading={card.heading}
                          label={{ value: card.date }}
                          link={{
                            isFullCardLink: true,
                            final_url: card.url,
                            accessibleDescription: withSmartling('mktg_read_more_about', {
                              title: card.heading || '',
                            }),
                          }}
                          media={{
                            image: cardImage,
                            isFullBleed: !isCaseStudyHub,
                          }}
                          tags={{
                            label: withSmartling('mktg_learning_campus_collection_tags'),
                            items: tags,
                          }}
                          text={{ html: card.subheading }}
                        />
                      );
                    })}
                  </Box>
                )}
              </SimplePagination>
            </>
          )}
        </Box>
      </Container>
    </Layout>
  );
};

export default CollectionBase;
